/**
 * Hotel Quality Landing Page Configuration
 * 
 * WINNING ANGLE: Comparison Value (Hotel Parity + Price Relief)
 * Generated: 161 purchases (highest-volume winning angle)
 * 
 * Key mechanics:
 * - "Same comfort" removes downgrade anxiety
 * - "Half Priced" reframes value without signaling cheapness
 * - "5-star secret" positions product as insider alternative
 * - Dominant emotions: Relief + Confidence
 */

import type { PartialLandingPageConfig } from './types';
import { createConfig } from './utils';

const hotelQualityPartial: PartialLandingPageConfig = {
  id: 'hotel-quality',
  name: 'Hotel Quality Pillow',
  slug: 'hotel-quality-pillow',
  category: 'angle',
  
  // Page-specific customizations for Hotel Quality
  quantityOptions: [2, 4], // Remove 1 Pillow option
  defaultQuantity: 2, // Default to 2 Pillows
  defaultSize: 'standard', // Standard as default
  sizeOrder: ['standard', 'king'], // Standard first
  hideFirstUpsell: false, // Show the single upsell (Extra Month)
  hideSecondUpsell: false, // N/A (second upsell removed)
  hideMostPopularBadge: true, // Hide "Most Popular" badge (only 2 quantity options)
  
  seo: {
    title: 'Same Comfort. Half the Price. | FluffCo Hotel Quality Pillow',
    description: 'The 5-star secret without the 5-star price. Same hotel-quality comfort at half the cost. 30-Night Better Sleep guarantee, free shipping.',
    keywords: ['hotel pillow', 'luxury pillow', '5-star hotel pillow', 'four seasons pillow', 'ritz carlton pillow', 'hotel quality bedding'],
  },
  
  // Gallery - Reordered for Hotel Quality page
  // Slide 1: Hotel Comfort (first)
  // Slide 2: Engineering Detail with pills (moved from position 3 to position 2)
  // Slide 3: Lifestyle Sleep with pills
  // Slide 4: Hotel Comparison
  // Slide 5: Comparison VS
  // Slide 6: Washing Machine
  // Slide 7-9: Expert slides with overlays (ExpertPillowSlide, CoupleSleepSlide, EthicalMakingSlide)
  gallery: {
    images: [
      // Slide 1: Hotel Comfort (was last, now first) - with modified title
      // Slide 2: Engineering Detail with pills (moved to position 2)
      { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new_3ea51b01.png', alt: '3-Layer Construction', type: 'image', badges: [{ type: 'text', position: 'bottom-left', content: '3 layers' }] },
      // Slide 3: Couple Sleep with pills (replaced lifestyle-sleep)
      { src: 'couple-sleep-slide', alt: 'Couple Sleep', type: 'couple-sleep' },
      // Slide 4: Hotel Comparison
      { src: '', alt: 'Hotel Comparison', type: 'hotel-comparison' },
      // Slide 5: Comparison
      { src: '', alt: 'Comparison', type: 'comparison' },
      // Slide 6: Washing Machine
      { src: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/RVxCLjqishtuSISc.png', alt: 'Easy Care', type: 'image', badges: [{ type: 'text', position: 'bottom-left', content: 'care' }] },
      // Slide 7: Expert Pillow Slide (component with overlays)
      { src: 'expert-pillow-slide', alt: 'Expert Pillow Features', type: 'expert-pillow' },
      // Slide 8: Ethical Making Slide (component with overlays)
      { src: 'ethical-making-slide', alt: 'Ethically Made', type: 'ethical-making' },
    ],
    // Use legacy default pills for the tips slot (slide 3)
    slotOverrides: {
      tips: {
        pills: [
          { number: 1, title: 'Elevate Your Sleep Game', description: 'Get the same high quality 5-star pillows as the big luxury resorts' },
          { number: 2, title: 'Wake Up Rejuvenated', description: 'Clear-eyed, pain free, rested and ready for the day' },
          { number: 3, title: 'Built to Last', description: 'Years of comfort' },
        ],
      },
      layers: {
        pills: [
          { number: 1, title: 'Support Layer', description: 'Firm foundation' },
          { number: 2, title: 'Comfort Core', description: 'Plush microfiber' },
          { number: 3, title: 'Cooling Shell', description: 'Breathable cotton' },
        ],
      },
      care: {
        pills: [
          { number: 1, title: 'Machine Wash', description: 'Easy cleaning' },
          { number: 2, title: 'Tumble Dry', description: 'Quick drying' },
          { number: 3, title: 'No Ironing', description: 'Ready to use' },
        ],
      },
      summary: {
        headline: '5-Star Hotel Comfort.\nHalf the Price.',
      },
    },
    showAwardCarousel: true,
    hideAwardCarouselMobile: true, // Hide award carousel on mobile for hotel-quality page
    awardCarouselItems: [
      { title: 'Best Overall Pillow', source: 'Apartment Therapy' },
      { title: 'Best Down Pillow', source: 'Men\'s Health' },
      { title: 'Best Soft Pillow', source: 'Architectural Digest' },
      { title: 'Editor\'s Pick', source: 'Healthline' },
      { title: '95/100 Rating', source: 'PureWow' },
    ],
  },
  
  hero: {
    // Updated headline per request
    headline: 'Hotel Quality Pillow. No Markups.',
    subheadline: 'The 5-star secret without the 5-star price tag. Finally, hotel-quality sleep delivered to your door.',
    trustBadge: {
      reviewCount: '546,000+',
      rating: '95/100',
      source: 'PureWow',
    },
    usps: [
      { icon: 'support', title: 'Same Hotel Comfort', description: 'Identical quality, half the cost' },
      { icon: 'shield', title: '50% Less Than Hotels', description: 'Direct pricing, no markup' },
      { icon: 'clock', title: '30-Night Better Sleep', description: 'Sleep better in 30 days or less' },
      { icon: 'location', title: 'Made in USA', description: 'Premium craftsmanship' },
    ],
    showOekoTexBadge: true,
    showOprahBadge: true,
    show30NightsBadge: true,
  },
  
  benefits: {
    sectionTitle: 'The 5-Star Secret',
    sectionSubtitle: 'Why pay hotel prices when you can get the same comfort for half?',
    benefits: [
      { icon: 'moon', title: 'Same Comfort, Your Bed', description: 'The exact same plush, supportive feel that makes hotel pillows unforgettable. Now yours every night.' },
      { icon: 'shield', title: 'Half the Price, Same Quality', description: 'We cut out the middlemen. Same suppliers as Four Seasons and Ritz-Carlton, 50%+ less.' },
      { icon: 'sparkles', title: 'Built to Hotel Standards', description: 'Designed to withstand commercial laundering. If it survives hotel housekeeping, it\'ll last you years.' },
    ],
  },
  
  difference: {
    standardPillowTitle: 'Hotel Store Pillows',
    standardPillowPoints: [
      'Premium prices for the same quality',
      'Paying for the brand name',
      'Same suppliers, higher markup',
      'No sleep guarantee',
      'Expensive shipping',
    ],
    fluffcoPillowTitle: 'FluffCo Pillow',
    fluffcoPillowPoints: [
      'Same comfort, half the price',
      'Direct from manufacturer',
      'Identical hotel-grade materials',
      '30-Night Sleep Guarantee',
      'Free shipping included',
    ],
  },
  
  testimonials: {
    sectionTitle: '546,000+ Made the Switch',
    sectionSubtitle: 'Same comfort. Half the price. See why they switched.',
    testimonials: [
      { name: 'Jennifer M.', location: 'San Francisco, CA', verified: true, content: 'I used to steal pillows from hotels (don\'t judge me 😅). Now I don\'t have to! These are EXACTLY like the ones at the Four Seasons. Same comfort, half the price. My husband thought I actually took them from our last trip!' },
      { name: 'Michael R.', location: 'New York, NY', verified: true, content: 'Business traveler here. Spent years wondering why I sleep better in hotels. Turns out it was the pillows. Same quality for half what I\'d pay at the hotel store. Best purchase I\'ve made.' },
      { name: 'Sarah & Tom K.', location: 'Denver, CO', verified: true, content: 'We honeymooned at the Ritz and couldn\'t stop talking about the pillows. Found FluffCo and got 4 for less than 2 would cost at the Ritz store. Same comfort, way better price 💕' },
      { name: 'Dr. Patricia L.', location: 'Boston, MA', verified: true, content: 'As a physician, I\'m skeptical of most sleep products. But these genuinely deliver hotel-quality comfort at half the price. I\'ve recommended them to patients. The value is remarkable.' },
    ],
    showMoreButton: true,
  },
  
  announcementBar: {
    text: 'Same Comfort. Half the Price.',
    showTimer: true,
    ctaText: 'Shop Now →',
    ctaTarget: 'pricing',
  },

};

export const hotelQualityConfig = createConfig(hotelQualityPartial);
export default hotelQualityConfig;
